import { queryKeys } from '@tms/api-client';
import { formatCalendarDate, newId, parseAmount } from '@tms/core';
import {
  AmountField,
  AppBar,
  Button,
  CategoryChipGrid,
  Input,
  PhoneShell,
  StampToggle,
} from '@tms/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { createEntry, getCategories } from '../../api';
import type { Summary } from '../../api';
import { routePaths } from '../../routes/routes';
import { newEntryCopy } from './copy';
import styles from './NewEntryScreen.module.css';
import { todayInIndia } from './today';

/**
 * `LED-02` — the screen the product exists for.
 *
 * **Debit is preselected**, because it is nine entries in ten and the screen book says so. Nothing below
 * the amount is required except a purpose: a person recording a bus fare should be able to do it in about
 * five seconds, and every field that demands attention is a second of that.
 *
 * **The entry appears instantly and reconciles.** The id is minted here, before the request is made
 * (`STD-002 §5`), which is what makes both halves of that possible: the optimistic row has the identity
 * the server will confirm, and a retry after a dropped connection is idempotent on the same value rather
 * than making a second entry (`FEAT-011 §5.2`).
 *
 * **A failed save restores the form with the values intact.** The screen navigates on success only, so
 * there is nothing to restore — the form was never left. That is deliberately simpler than saving and
 * re-populating: a form that stays put cannot lose anything.
 */
export default function NewEntryScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<'debit' | 'credit'>('debit');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [occurredOn] = useState(todayInIndia);
  const [submitted, setSubmitted] = useState(false);

  /**
   * The entry's id, minted **once for this form** rather than once per send.
   *
   * That is the whole of the idempotency contract working (`FEAT-011 §5.2`). A double tap, a retry after a
   * dropped connection, a resend from an offline queue — all carry this same value, and the server answers
   * the second one with the entry the first one made instead of creating a duplicate. Minting it inside
   * the mutation would have given two taps two ids and two entries, which is the exact bug the
   * client-minted id exists to prevent.
   */
  // `newId` rather than `crypto.randomUUID()`: a v4 is unique but not time-ordered, and `ARCH-002 §1`
  // wants the same ULID shape the server's own generator produces so an entry made on a phone and one made
  // on the server land in the same index the same way (`T-0308`).
  const [publicId] = useState(() => newId());

  const categories = useQuery({
    queryKey: queryKeys.categories.list(),
    queryFn: async () => {
      const result = await getCategories();
      if (!result.ok) {
        throw result.failure;
      }

      return result.value;
    },
  });

  // Only the purposes of the kind being recorded. `FEAT-011 §3.7` — a debit entry takes a debit purpose,
  // and offering the others would be offering a choice the server refuses.
  const choices = (categories.data ?? [])
    .filter((category) => category.kind === (kind === 'credit' ? 'Credit' : 'Debit'))
    .map((category) => ({ id: category.publicId, name: category.name, icon: category.icon }));

  const amountMinor = parseAmount(amount);

  const save = useMutation({
    mutationFn: async () => {
      const result = await createEntry({
        publicId,
        kind: kind === 'credit' ? 'Credit' : 'Debit',
        amountMinor: amountMinor ?? 0,
        categoryId: categoryId ?? '',
        occurredOn,
        ...(note.trim() ? { note: note.trim() } : {}),
      });

      if (!result.ok) {
        throw result.failure;
      }

      return result.value;
    },

    onSuccess: (entry) => {
      // The optimistic part: the new entry is written into the cached summary before the ledger is
      // refetched, so Home shows it the moment this screen closes rather than a beat later. The
      // invalidation below then reconciles against what the server actually stored — the totals in
      // particular, which this cannot compute without knowing the payday window.
      queryClient.setQueryData<Summary>(queryKeys.ledger.balance(), (current) =>
        current ? { ...current, recent: [entry, ...current.recent].slice(0, 4) } : current,
      );

      void queryClient.invalidateQueries({ queryKey: queryKeys.ledger.all });
      void navigate(routePaths.home);
    },
  });

  // Shown only after an attempt. A form that turns red as somebody types has told them off for not having
  // finished yet (`STD-002 §7`).
  const amountError = submitted && (amountMinor === null || amountMinor <= 0) ? newEntryCopy.amountRequired : null;
  const categoryError = submitted && !categoryId ? newEntryCopy.categoryRequired : null;

  return (
    <PhoneShell
      appBar={
        <AppBar
          title={newEntryCopy.eyebrow}
          backKind="dismiss"
          backLabel={newEntryCopy.dismiss}
          onBack={() => {
            void navigate(routePaths.home);
          }}
        />
      }
    >
      <h1 className={styles.title}>{newEntryCopy.title}</h1>

      <div className={styles.field}>
        <StampToggle
          value={kind}
          onChange={(next) => {
            setKind(next);

            // The purposes are per kind, so the one that was chosen may not exist in the other list.
            // Clearing is the honest answer; keeping it would send a debit purpose on a credit entry and
            // collect a `422` for something the person could not see.
            setCategoryId(null);
          }}
        />
      </div>

      <div className={styles.field}>
        <AmountField
          value={amount}
          onChange={setAmount}
          label={newEntryCopy.amountLabel}
          autoFocus
          {...(amountError ? { error: amountError } : {})}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.label}>{newEntryCopy.dateLabel}</span>
        <p className={styles.date}>{formatCalendarDate(occurredOn)}</p>
      </div>

      <div className={styles.field}>
        <CategoryChipGrid
          categories={choices}
          value={categoryId}
          onChange={setCategoryId}
          label={newEntryCopy.purposeLabel}
          loading={categories.isPending}
          {...(categories.isError ? { error: newEntryCopy.categoriesFailed } : {})}
        />
        {categoryError ? (
          <p className={styles.error} role="alert">
            {categoryError}
          </p>
        ) : null}
      </div>

      <div className={styles.field}>
        <Input
          label={newEntryCopy.noteLabel}
          value={note}
          maxLength={80}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
      </div>

      {save.isError ? (
        <p className={styles.error} role="alert" data-testid="new-entry-error">
          {newEntryCopy.saveFailed}
        </p>
      ) : null}

      <Button
        block
        busy={save.isPending}
        data-testid="new-entry-submit"
        onClick={() => {
          setSubmitted(true);

          // `Button` disables while busy, so this is belt and braces — but a disabled button is a render
          // away and a second tap can land before it. The id above means a request that slipped through
          // would be idempotent anyway; this stops it being sent at all.
          if (save.isPending || amountMinor === null || amountMinor <= 0 || !categoryId) {
            return;
          }

          save.mutate();
        }}
      >
        {newEntryCopy.submit}
      </Button>
    </PhoneShell>
  );
}
